<template>
    <div class='container'>
        <div class="content-container">
            <div class="title-container">
                <span>
                    {{ $t('factures.title') }}
                    <ReglePastille cle="facturesFournisseur.definition" />
                </span>
            </div>
            <div class="filter-container">
                <FilterFournisseur
                    @search="search"
                    @addFilterStatus="addFilterStatus"
                    @removeFilterStatus="removeFilterStatus"
                    @clearFilter="clearFilter"
                    @statusToggleSelectAll="statusToggleSelectAll"
                    @societeToggleSelectAll="societeToggleSelectAll"
                    @setFactureDateFrom="this.setFactureDateFrom"
                    @setFactureDateTo="this.setFactureDateTo"
                    @comparison="setComparison"
                    @comparisonValue="setComparisonValue"
                    @addFilterSociete="addSociete"
                    @removeFilterSociete="removeSociete"
                    @setNumero="setNumero"
                    @multipleSelectSearchSociete="multipleSelectSearchSociete"
                    :search-societe="searchSociete"
                    :comparison-list-facture="comparisonListFacture"
                    :filter="filter"
                    :statusList="statusList"
                    :societe-list="societeList"
                    :societe-list-total="societeListTotal"
                />
            </div>
            <div class="dataTable">
                <div class="headerWrapper">
                    <h2>
                        {{ this.count }}
                        {{ $tc('factures.fournisseur.dataTable.title', this.count) }}
                        <span class="regles-pastilles-group">
                            <ReglePastille cle="facturesFournisseur.filtreParDefaut" />
                            <ReglePastille cle="facturesFournisseur.statutRejeteeExclusif" />
                        </span>
                    </h2>
                    <BorderedIconButton
                        v-if="tableData.length > 0"
                        class="export-button"
                        @click="exportXls"
                        :imgUrl="iconeXls" :title="$t('factures.export')"/>
                </div>
                <LoaderComponent v-if="tableData.length === 0 && isLoading" class="loader" />
                <div v-else @scroll="handleScroll" ref="content" class="datatable-container">
                    <DataTable
                        :count="+count"
                        :tableData="tableData"
                        :tableHeader="tableHeader"
                        :isLoading="isLoading"
                        :stop-propa-on-pdf="true"
                        @sort="doSort"
                        @select="goToPage"
                    />
                </div>
            </div>
            <WaitingModal v-if="isExportXlsLoading"/>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import { FACTURE_FOURNISSEUR } from '@/modules/FacturesFournisseurs/store/mutation-types';
import FilterFournisseur from '@/modules/Common/components/FilterFournisseur.vue';
import { FactureFournisseurDTO, Operateur, StatusFacture } from '@/models/FactureFournisseurDTO';
import { SocieteDTO } from '@/models/SocieteDTO';
import { FactureFournisseurActions } from '@/modules/FacturesFournisseurs/store/action-types';
import { SortType, sortValue } from '@/models/SortType';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import router from '@/router';
import DataTable from '@/components/ui-components/DataTable.vue';
import { statusFournisseurName } from '@/components/ui-components/StatusTagFournisseur.vue';
import AccountService from '@/modules/Account/services/AccountService';
import { store } from '@/plugins/store';
import { MENU } from '@/components/layout/store/mutation-types';
import { MENU_FOURNISSEUR, MENU_FOURNISSEUR_SWITCH_CLIENT } from '@/components/layout/store/state';
import dayjs from 'dayjs';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';

interface Data {
    statusList: StatusFacture[];
    societeList: SocieteDTO[];
    societeListTotal: SocieteDTO[];
    filter: {
        comparisonFacture: string,
        comparisonValue?: number,
        societes: SocieteDTO[],
        numeroFacture: string,
        statusList: StatusFacture[],
        factureDateFrom: string,
        factureDateTo: string
    };
    comparisonListFacture: string[];
    searchSociete: string;
}

export default defineComponent({
    name: 'FacturesFournisseurList',
    components: { WaitingModal, BorderedIconButton, LoaderComponent, DataTable, FilterFournisseur },
    data(): Data {
        return {
            statusList: Object.values(StatusFacture),
            societeList: [],
            societeListTotal: [],
            filter: {
                comparisonFacture: this.$t('facture.suivi.dropdown.novalue'),
                comparisonValue: undefined,
                societes: [],
                numeroFacture: '',
                statusList: [],
                factureDateFrom: '',
                factureDateTo: ''
            },
            comparisonListFacture: [
                this.$t('facture.suivi.dropdown.novalue'),
                this.$t('label.fournisseur.dropdown.lessthan'),
                this.$t('label.fournisseur.dropdown.greaterthan'),
            ],
            searchSociete: '',
        };
    },
    setup() {
        const content = ref<Element>();

        onMounted(() => {
            content.value;
        });

        return { content };
    },
    async beforeMount() {
        if (localStorage.getItem('isFournisseur') === 'true' && localStorage.getItem('isClient') === 'true') {
            store.commit(`menuState/${MENU.SET_MENU}`, MENU_FOURNISSEUR_SWITCH_CLIENT);
        } else {
            store.commit(`menuState/${MENU.SET_MENU}`, MENU_FOURNISSEUR);
        }
        this.filter.statusList = this.filterStatus?.length > 0 ? this.filterStatus : this.statusList.filter(status => status !== StatusFacture.rejetee);
        this.filter.factureDateFrom = this.filterDateDebut;
        this.getFactures();
        AccountService.getSocieteIds().then((societes: any) => {
            this.societeList = societes.data;
        });
    },
    computed: {
        iconeXls() {
            return require('@/assets/img/xls.svg');
        },
        sort(): {
            sortData: string;
            sortStatus: SortType;
        } {
            return this.$store.getters['factureFournisseurState/getFactureSort'];
        },
        tableData() {
            if (this.$store.getters['factureFournisseurState/getFactures'] == undefined) {
                return [];
            }
            return this.$store.getters['factureFournisseurState/getFactures'].map(
                ({id, societe, fournisseur,  date, numero, dateEcheance, montantTTC, montantRestantTTC, dateDernierReglement, statut, devise, typeFacture, identifiantFacture}: FactureFournisseurDTO) => {
                    const statutFournisseur = statusFournisseurName(statut.toString());

                    return {
                        idMasque: id == null ? identifiantFacture : id,
                        societe: societe,
                        fournisseur: fournisseur,
                        date: date ? dayjs(date).format('DD/MM/YYYY') : '',
                        numero: numero,
                        echeance: dateEcheance ? dayjs(dateEcheance).format('DD/MM/YYYY') : '',
                        montant: montantTTC + ' ' + this.$t('devise.' + devise),
                        solde: montantRestantTTC != null ? (montantRestantTTC + ' ' + this.$t('devise.' + devise)) : '',
                        dernierReglement: dateDernierReglement ? dayjs(dateDernierReglement).format('DD/MM/YYYY') : '',
                        type: typeFacture,
                        statutFournisseur
                    };
                }
            );
        },
        tableHeader() {
            return this.$store.getters['factureFournisseurState/getHeader'];
        },
        isLoading(): boolean {
            return this.$store.getters['factureFournisseurState/isLoading'];
        },
        getPage(): number {
            return this.$store.getters['factureFournisseurState/getFacturePage'];
        },
        getNumberOfPages(): number {
            return this.$store.getters['factureFournisseurState/getFactureNumberOfPage'];
        },
        count() {
            return this.$store.getters['factureFournisseurState/getFactureCount'];
        },
        filterStatus(): StatusFacture[] {
            return this.$store.getters['factureFournisseurState/getFacturesStatusList'];
        },
        filterDateDebut(): string {
            return this.$store.getters['factureFournisseurState/getDateDebut'];
        },
        isExportXlsLoading(): boolean {
            return this.$store.getters['factureFournisseurState/isXlsLoading'];
        },
    },
    methods: {
        getFactures(page = 0) {
            this.$store.dispatch(`factureFournisseurState/${FactureFournisseurActions.GET_FACTURES}`, {
                page,
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                statut: this.filter.statusList.length === this.statusList.length ? [] : this.filter.statusList,
                dateDebut: this.filter.factureDateFrom,
                dateFin: this.filter.factureDateTo,
                societeIds: this.filter.societes.length === this.societeList?.length ? [] : this.filter.societes.map(societe => societe.id),
                montant: this.filter.comparisonValue,
                operateur: this.getOperateur(),
                numero: this.filter.numeroFacture
            });
        },
        getOperateur() {
            switch (this.filter.comparisonFacture) {
                case ' - ':
                    return null;
                case 'Inférieur ou égal':
                    return Operateur.inferieur;
                case 'Supérieur ou égal':
                    return Operateur.superieur;
            }
        },
        search() {
            this.$store.commit(`factureFournisseurState/${FACTURE_FOURNISSEUR.RESET_ITEMS}`);
            this.doFilter();
        },
        doFilter() {
            this.$store.commit(`factureFournisseurState/${FACTURE_FOURNISSEUR.SET_FILTER_OPERATEUR}`, this.filter.comparisonFacture);
            this.$store.commit(`factureFournisseurState/${FACTURE_FOURNISSEUR.SET_FILTER_MONTANT}`, this.filter.comparisonValue);
            this.$store.commit(`factureFournisseurState/${FACTURE_FOURNISSEUR.SET_FILTER_SOCIETE}`, this.filter.societes);
            this.$store.commit(`factureFournisseurState/${FACTURE_FOURNISSEUR.SET_FILTER_NUMERO_FACTURE}`, this.filter.numeroFacture);
            this.$store.commit(`factureFournisseurState/${FACTURE_FOURNISSEUR.SET_FILTER_ETAT}`, this.filter.statusList);
            this.$store.commit(`factureFournisseurState/${FACTURE_FOURNISSEUR.SET_FILTER_DATE_DEBUT}`, this.filter.factureDateFrom);
            this.$store.commit(`factureFournisseurState/${FACTURE_FOURNISSEUR.SET_FILTER_DATE_FIN}`, this.filter.factureDateTo);
            this.getFactures();
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            const { sortData, sortStatus } = sort;
            this.$store.commit(`factureFournisseurState/${FACTURE_FOURNISSEUR.SET_SORT}`, { sortData, sortStatus });
            this.$store.commit(`factureFournisseurState/${FACTURE_FOURNISSEUR.RESET_ITEMS}`);
            this.getFactures();
        },
        addFilterStatus(status: StatusFacture) {
            if (status === StatusFacture.rejetee) {
                this.filter.statusList = [StatusFacture.rejetee];
            } else {
                this.filter.statusList = this.filter.statusList.filter(status => status !== StatusFacture.rejetee);
                this.filter.statusList = [...this.filter.statusList, status];
            }
        },
        removeFilterStatus(status: string) {
            this.filter.statusList = this.filter.statusList.filter(statusData => statusData !== status);
            if (this.filter.statusList.length === 0) {
                this.filter.statusList = this.statusList.filter(status => status !== StatusFacture.rejetee);
            }
        },
        statusToggleSelectAll() {
            // Du fait que les factures rejetées ne sont pas sur la même table, la pagination est difficile à gérer.
            // Il a donc été décidé d'afficher les factures rejetées seules
            this.filter.statusList = this.statusList.filter(status => status !== StatusFacture.rejetee);
        },
        societeToggleSelectAll() {
            this.filter.societes = this.filter.societes.length === this.societeList.length ? [] : this.societeList;
        },
        setFactureDateFrom(value: string): void {
            this.filter.factureDateFrom = value;
        },
        setFactureDateTo(value: string): void {
            this.filter.factureDateTo = value;
        },
        setComparison(comparison: string) {
            this.filter.comparisonFacture = comparison;
        },
        setComparisonValue(comparisonValue: number) {
            this.filter.comparisonValue = comparisonValue;
        },
        setNumero(numeroFacture: string) {
            this.filter.numeroFacture = numeroFacture;
        },
        clearFilter() {
            this.filter.comparisonFacture = '';
            this.filter.statusList = this.statusList.filter(status => status !== StatusFacture.rejetee);
            this.filter.comparisonValue = undefined;
            this.filter.factureDateFrom = '';
            this.filter.factureDateTo = '';
            this.filter.societes = [];
            this.filter.numeroFacture = '';

            this.doFilter();
        },
        addSociete(societe: SocieteDTO) {
            this.filter.societes.push(societe);
        },
        removeSociete(societe: SocieteDTO) {
            this.filter.societes = this.filter.societes.filter(itemSociete => itemSociete !== societe);
        },
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            this.$store.commit(`factureFournisseurState/${FACTURE_FOURNISSEUR.SET_SCROLL}`, top);
            if (max - top <= height * 1.5 && !this.isLoading && this.getPage + 1 < this.getNumberOfPages) {
                this.getFactures(this.getPage + 1);
            }
        },
        goToPage(item: { statutFournisseur: string, idMasque: number }){
            const isFactureRejetee: boolean = item.statutFournisseur === 'Rejetée';
            router.push({ name: 'factureFournisseurPage', params: { id: item.idMasque }, query: {isFactureRejetee: isFactureRejetee.toString()} });
        },
        multipleSelectSearchSociete(search: string) {
            const societeFiltre = this.societeList?.filter(societe=> societe.libelle.toLowerCase().includes(search.toLowerCase()));
            this.filter.societes = societeFiltre === undefined ? [] : societeFiltre;
        },
        exportXls() {
            this.$store.dispatch(`factureFournisseurState/${FactureFournisseurActions.GET_XLS}`, {
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                statut: this.filter.statusList.length === this.statusList.length ? [] : this.filter.statusList,
                dateDebut: this.filter.factureDateFrom,
                dateFin: this.filter.factureDateTo,
                societeIds: this.filter.societes.length === this.societeList?.length ? [] : this.filter.societes.map(societe => societe.id),
                montant: this.filter.comparisonValue,
                operateur: this.getOperateur(),
                numero: this.filter.numeroFacture
            });
        }
    }
});
</script>

<style scoped lang='scss'>
@import '@/theme/pages-recherche.scss';
.regles-pastilles-group {
    display: inline-flex;
    align-items: center;
    gap: 16px;
}
.margin {
    margin: 10px 10px 10px 20px;
}
.filter-container {
    display: flex;
    padding: 3px 16px;
    background: white;
    border-radius: 6px;
    margin-bottom: 6px;
}
.datatable-container {
    max-height: calc(100vh - 370px);
}

@media screen and (max-width: 1531px) {
    .datatable-container {
        max-height: calc(100vh - 470px);
    }
}

@media screen and (max-width: 970px) {
    .datatable-container {
        max-height: calc(100vh - 528px);
    }
}
.filter {
    width: 100%;
    margin-bottom: 0;
    padding: 0 16px;
}
.container {
    padding-top: 8px;
}
.title-container {
    margin-bottom: 8px;
}
.filter-container {
    padding-top: 2px;
}
:deep(.filterAndButtons) {
    margin-bottom: 0;
}
.dataTable {
    padding-top: 0;
}
:deep(table tr *) {
    padding: 0 3px;
}
:deep(.containerBtn) {
    height: 16px;
}
.export-button{
    height: 32px;
}
</style>
